import { useEffect, useMemo, useRef, useState } from "react";
import Picker from "react-mobile-picker";
import { X } from "lucide-react";

const isTouchDevice = () => {
  if (typeof window === "undefined") return false;
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
};

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function toFixedNumber(value, decimals) {
  const fixed = Number.parseFloat(value).toFixed(decimals);
  // Remove trailing zeros if decimals is 0
  return decimals > 0 ? fixed : String(Math.round(Number(fixed)));
}

export default function LongPressNumericPickerInput({
  value,
  onChange,
  placeholder = "",
  min = 0,
  max = 100,
  step = 1,
  decimals = 0,
  unit = "",
  longPressMs = 300,
  name,
  inputClassName = "form-input",
  containerClassName = "relative",
}) {
  const [open, setOpen] = useState(false);
  const [tempValue, setTempValue] = useState("");
  const [touchActive, setTouchActive] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const pressTimerRef = useRef(null);
  const pointerActiveRef = useRef(false);
  const inputRef = useRef(null);
  const startYRef = useRef(0);
  const touchIdRef = useRef(null);
  const baseIndexRef = useRef(0);

  const numericPlaceholder = useMemo(() => {
    if (!placeholder) return undefined;
    const parsed = Number.parseFloat(
      String(placeholder).replace(/[^0-9.\-]/g, "")
    );
    return Number.isFinite(parsed) ? parsed : undefined;
  }, [placeholder]);

  const options = useMemo(() => {
    const values = [];
    if (max < min || step <= 0) return values;
    // Guard against floating precision issues
    const count = Math.floor((max - min) / step) + 1;
    for (let i = 0; i < count; i += 1) {
      const v = min + i * step;
      values.push(toFixedNumber(v, decimals));
    }
    return values;
  }, [min, max, step, decimals]);

  // Optimize very large ranges (e.g., weight 1-300 by 0.1) using two columns
  const useTwoColumn = useMemo(() => {
    const optionCount = Math.floor((max - min) / step) + 1;
    return decimals === 1 && step === 0.1 && optionCount > 1000;
  }, [min, max, step, decimals]);

  const integerOptions = useMemo(() => {
    if (!useTwoColumn) return [];
    const arr = [];
    for (let v = Math.floor(min); v <= Math.floor(max); v += 1)
      arr.push(String(v));
    return arr;
  }, [useTwoColumn, min, max]);

  const decimalOptions = useMemo(() => {
    if (!useTwoColumn) return [];
    return Array.from({ length: 10 }, (_, i) => String(i));
  }, [useTwoColumn]);

  const findIndexForValue = (val) => {
    if (val == null || val === "") return 0;
    const num = Number.parseFloat(val);
    if (Number.isFinite(num)) {
      const idx = Math.round((num - min) / step);
      return Math.max(0, Math.min(options.length - 1, idx));
    }
    const idx2 = options.indexOf(String(val));
    return idx2 >= 0 ? idx2 : 0;
  };

  useEffect(() => {
    if (!open) return;
    function preventScroll(e) {
      // Prevent background scrolling while picker is open during interaction
      if (touchActive) e.preventDefault();
    }
    document.body.style.overflow = "hidden";
    window.addEventListener("touchmove", preventScroll, { passive: false });
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("touchmove", preventScroll);
    };
  }, [open, touchActive]);

  const startPressTimer = () => {
    if (!isTouchDevice()) return; // Do not open picker on desktop
    clearTimeout(pressTimerRef.current);
    pressTimerRef.current = setTimeout(() => {
      pointerActiveRef.current = true;
      setTouchActive(true);
      const rect = inputRef.current?.getBoundingClientRect();
      setAnchorRect(rect || null);
      const initial =
        value != null && value !== ""
          ? Number.parseFloat(value)
          : numericPlaceholder != null
          ? numericPlaceholder
          : min;
      const clamped = clamp(initial, min, max);
      const formatted = toFixedNumber(clamped, decimals);
      setTempValue(formatted);
      baseIndexRef.current = findIndexForValue(formatted);
      setOpen(true);
    }, longPressMs);
  };

  const cancelPressTimer = () => {
    clearTimeout(pressTimerRef.current);
  };

  const handleTouchStart = (e) => {
    const t = e.touches && e.touches[0];
    if (t) {
      startYRef.current = t.clientY;
      touchIdRef.current = t.identifier;
    }
    startPressTimer();
  };

  const handleTouchMove = () => {
    // If user moves before long press threshold and not open yet, cancel
    if (!open) cancelPressTimer();
  };

  const commitAndClose = () => {
    if (open) {
      onChange?.(tempValue);
      setOpen(false);
    }
    setTouchActive(false);
    pointerActiveRef.current = false;
  };

  const handleTouchEnd = () => {
    cancelPressTimer();
    if (pointerActiveRef.current) commitAndClose();
  };

  useEffect(() => {
    if (!open) return;
    const onDocMove = (e) => {
      const touches = e.touches ? Array.from(e.touches) : [];
      const t =
        touches.find((tt) => tt.identifier === touchIdRef.current) ||
        touches[0];
      if (!t) return;
      const dy = t.clientY - startYRef.current;
      const sensitivityPx = 28; // px per step
      const stepsMoved = Math.round(-dy / sensitivityPx);
      if (useTwoColumn) {
        const decCount = 10;
        const current = Number.parseFloat(tempValue || String(min));
        const baseCombined = Math.round((current - Math.floor(min)) * decCount);
        const maxCombined = (Math.floor(max) - Math.floor(min)) * decCount;
        const newCombined = Math.max(
          0,
          Math.min(maxCombined, baseCombined + stepsMoved)
        );
        const iIdx = Math.floor(newCombined / decCount);
        const dIdx = newCombined % decCount;
        const intVal = Math.floor(min) + iIdx;
        const decVal = dIdx / decCount;
        const next = Math.min(max, Math.max(min, intVal + decVal));
        setTempValue(toFixedNumber(next, decimals));
      } else {
        const newIndex = Math.max(
          0,
          Math.min(options.length - 1, baseIndexRef.current + stepsMoved)
        );
        setTempValue(options[newIndex]);
      }
      e.preventDefault();
    };
    const onDocEnd = () => {
      commitAndClose();
    };
    document.addEventListener("touchmove", onDocMove, { passive: false });
    document.addEventListener("touchend", onDocEnd, { passive: false });
    document.addEventListener("touchcancel", onDocEnd, { passive: false });
    return () => {
      document.removeEventListener("touchmove", onDocMove);
      document.removeEventListener("touchend", onDocEnd);
      document.removeEventListener("touchcancel", onDocEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, options, useTwoColumn, tempValue, min, max, decimals]);

  const showClear = value != null && value !== "";

  // Desktop: normal input
  if (!isTouchDevice()) {
    return (
      <div className={containerClassName}>
        <input
          ref={inputRef}
          name={name}
          type="number"
          step={step}
          min={min}
          max={max}
          className={`${inputClassName} pr-8`}
          placeholder={placeholder}
          value={value ?? ""}
          onChange={(e) => onChange?.(e.target.value)}
        />
        {showClear && (
          <button
            type="button"
            aria-label="Clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            onClick={() => onChange?.("")}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }

  // Mobile/touch: long-press to open wheel picker
  return (
    <div className={containerClassName}>
      <input
        ref={inputRef}
        name={name}
        type="text"
        readOnly
        className={`${inputClassName} pr-8`}
        style={{
          WebkitUserSelect: "none",
          WebkitTouchCallout: "none",
          touchAction: "manipulation",
        }}
        placeholder={placeholder}
        value={value ?? ""}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      />
      {showClear && (
        <button
          type="button"
          aria-label="Clear"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          onClick={() => onChange?.("")}
        >
          <X className="h-4 w-4" />
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50"
          style={{ overscrollBehavior: "contain" }}
        >
          {/* Backdrop to block scroll and interactions */}
          <div
            className="absolute inset-0 bg-black/30"
            onTouchMove={(e) => e.preventDefault()}
            onClick={commitAndClose}
          />
          {/* Anchored panel near the input */}
          <div
            className="absolute bg-white rounded-xl shadow-lg p-3"
            style={{
              left: anchorRect ? `${anchorRect.left}px` : "50%",
              top: anchorRect
                ? `${anchorRect.top + anchorRect.height / 2}px`
                : "50%",
              width: anchorRect ? `${anchorRect.width}px` : "280px",
              transform: anchorRect
                ? "translateY(-50%)"
                : "translate(-50%, -50%)",
              touchAction: "none",
            }}
            onTouchMove={(e) => e.preventDefault()}
          >
            <div className="text-center text-sm text-gray-500 mb-2 select-none">
              Slide to select {unit ? `(${unit})` : ""}. Release to confirm.
            </div>
            {useTwoColumn ? (
              <Picker
                value={{
                  i: String(
                    Math.floor(
                      Number.parseFloat(tempValue || String(min)) || min
                    )
                  ),
                  d: String(
                    Math.round(
                      ((Number.parseFloat(tempValue || String(min)) || min) %
                        1) *
                        10
                    )
                  ),
                }}
                onChange={(vObj) => {
                  const intNum = Number(vObj.i);
                  const decNum = Number(vObj.d) / 10;
                  const next = Math.min(max, Math.max(min, intNum + decNum));
                  setTempValue(toFixedNumber(next, decimals));
                }}
                wheelMode="normal"
              >
                <Picker.Column name="i">
                  {integerOptions.map((opt) => (
                    <Picker.Item key={opt} value={opt}>
                      {opt}
                    </Picker.Item>
                  ))}
                </Picker.Column>
                <Picker.Column name="d">
                  {decimalOptions.map((opt) => (
                    <Picker.Item key={opt} value={opt}>
                      .{opt}
                    </Picker.Item>
                  ))}
                </Picker.Column>
              </Picker>
            ) : (
              <Picker
                value={{ v: tempValue }}
                onChange={(valObj) => setTempValue(String(valObj.v))}
                wheelMode="normal"
              >
                <Picker.Column name="v">
                  {options.map((opt) => (
                    <Picker.Item key={opt} value={opt}>
                      {opt}
                    </Picker.Item>
                  ))}
                </Picker.Column>
              </Picker>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
